<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatNumber, FEATURE_COLORS } from '../utils/costCalculator';

const props = defineProps({
  byFeature: { type: Object, default: () => ({}) },
});

const { t } = useI18n();

const featureLabel = feature => {
  const key = `CAPTAIN_SETTINGS.FEATURES.${feature.toUpperCase()}.TITLE`;
  const translated = t(key);
  return translated === key
    ? feature.charAt(0).toUpperCase() + feature.slice(1).replace(/_/g, ' ')
    : translated;
};

const breakdown = computed(() => {
  const entries = Object.entries(props.byFeature);
  if (entries.length === 0) return [];
  const total = entries.reduce((sum, [, tokens]) => sum + tokens, 0) || 1;
  return entries
    .map(([feature, tokens]) => ({
      feature,
      tokens,
      percent: Math.round((tokens / total) * 100),
      color: FEATURE_COLORS[feature] || 'bg-n-slate-9',
      label: featureLabel(feature),
    }))
    .sort((a, b) => b.tokens - a.tokens);
});

const hasData = computed(() => breakdown.value.length > 0);
</script>

<template>
  <div>
    <div v-if="hasData" class="flex flex-col gap-3">
      <div
        v-for="item in breakdown"
        :key="item.feature"
        class="flex flex-col gap-1"
      >
        <div class="flex items-center justify-between text-sm">
          <span class="text-n-slate-12">{{ item.label }}</span>
          <span class="text-n-slate-11">
            {{ formatNumber(item.tokens) }} tokens ({{ item.percent }}%)
          </span>
        </div>
        <div class="w-full h-2 rounded-full bg-n-alpha-black2 overflow-hidden">
          <div
            class="h-full rounded-full transition-all duration-300"
            :class="item.color"
            :style="{ width: `${Math.max(item.percent, 1)}%` }"
          />
        </div>
      </div>
    </div>

    <div v-else class="text-sm text-n-slate-11 py-4 text-center">
      {{ t('CAPTAIN_SETTINGS.USAGE.BREAKDOWN.NO_DATA') }}
    </div>
  </div>
</template>
